import { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Input, InputNumber, Tabs, Modal, Form, Upload,
  Space, message, Popconfirm, Typography,
} from 'antd';
import {
  PlusOutlined, UploadOutlined, DownloadOutlined, SearchOutlined,
} from '@ant-design/icons';
import { api } from '../../api';

const { Title, Text } = Typography;

export default function SlowInventory() {
  const [activeTab, setActiveTab] = useState('my');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [myData, setMyData] = useState([]);
  const [sharedData, setSharedData] = useState([]);
  const [filterModel, setFilterModel] = useState('');

  // 新增/编辑弹窗
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [form] = Form.useForm();

  // 批量导入弹窗
  const [importOpen, setImportOpen] = useState(false);

  // 共享数据导出中
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async (scope, model = '') => {
    setLoading(true);
    // 先清空当前列表，避免残留上次查询结果
    if (scope === 'my') setMyData([]);
    else setSharedData([]);
    try {
      const { data } = await api.get('/slow-inventory/records', {
        params: { page: 1, pageSize: 1000, model: (model || '').trim(), scope },
      });
      const rows = data.data || [];
      if (scope === 'my') setMyData(rows);
      else setSharedData(rows);
    } catch (e) {
      message.error(e.response?.data?.detail || '查询数据失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(activeTab, '');
  }, [activeTab, fetchData]);

  const handleSearch = () => {
    fetchData(activeTab, filterModel);
  };

  const handleTabChange = (key) => {
    setFilterModel('');
    setActiveTab(key);
  };

  // 同步功能临时下线（隐藏入口），逻辑保留便于恢复
  // const handleSync = async () => { ... };

  // 列宽自适应：canvas 用与表格相同的字体栈实测文本像素宽（精确不换行）
  const measureText = (() => {
    let ctx = null;
    return (s) => {
      if (!ctx) {
        ctx = document.createElement('canvas').getContext('2d');
        ctx.font = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "PingFang SC", "Microsoft YaHei", "Noto Sans", sans-serif';
      }
      return Math.ceil(ctx.measureText(String(s ?? '')).width);
    };
  })();
  // +32：单元格左右 padding 16 + 渲染余量 16；客户名称不限上限，保证单行完整显示
  const fitWidth = (rows, key, min = 90, max = Infinity) =>
    rows.length ? Math.min(max, Math.max(min, ...rows.map((r) => measureText(r[key]) + 32))) : min;

  const columns = (rows, editable) => {
    const cols = [
      // 内容列：宽度随数据自适应（最长内容决定），单行完整显示
      { title: '客户名称', dataIndex: 'custom', align: 'center', width: fitWidth(rows, 'custom') },
      { title: '型号', dataIndex: 'model', align: 'center', width: fitWidth(rows, 'model', 100, 400) },
      { title: '数量', dataIndex: 'number', align: 'center', width: fitWidth(rows, 'number', 100, 160) },
      { title: '效期', dataIndex: 'valid', align: 'center', width: fitWidth(rows, 'valid', 110, 200) },
      { title: '创建时间', dataIndex: 'createTime', align: 'center', width: 170 },
      { title: '修改时间', dataIndex: 'updateTime', align: 'center', width: 170 },
      // 备注：按内容自适应（设上限），置于末尾（操作列之前）
      { title: '备注', dataIndex: 'remark', align: 'center', width: fitWidth(rows, 'remark', 160, 320), ellipsis: true },
    ];
    if (editable) {
      cols.push({
        title: '操作', align: 'center', width: 140, fixed: 'right',
        render: (_, row) => (
          <Space>
            <Button type="link" size="small" onClick={() => openEdit(row)}>编辑</Button>
            <Popconfirm title="确认删除该记录？" onConfirm={() => handleDelete(row)}>
              <Button type="link" size="small" danger>删除</Button>
            </Popconfirm>
          </Space>
        ),
      });
    }
    return cols;
  };

  const openAdd = () => {
    setIsEdit(false);
    form.setFieldsValue({ model: '', number: 1, valid: '', remark: '' });
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setIsEdit(true);
    form.setFieldsValue({
      __id: row.id, model: row.model, number: row.number,
      valid: row.valid, remark: row.remark,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/slow-inventory/records/${values.__id}`, values);
        message.success('已保存');
      } else {
        await api.post('/slow-inventory/records', values);
        message.success('已新增');
      }
      setDialogOpen(false);
      fetchData(activeTab, filterModel);
    } catch (e) {
      message.error(e.response?.data?.detail || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    try {
      await api.delete(`/slow-inventory/records/${row.id}`);
      message.success('已删除');
      fetchData(activeTab, filterModel);
    } catch (e) {
      message.error(e.response?.data?.detail || '删除失败');
    }
  };

  const handleDownload = async () => {
    try {
      const res = await api.get('/slow-inventory/template', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = '滞销库存导入模板.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      message.error('模板下载失败');
    }
  };

  // 共享数据导出：导出全部共享数据（Excel）
  const handleExportShared = async () => {
    setExporting(true);
    try {
      const res = await api.get('/slow-inventory/export', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const now = new Date();
      const pad = (n) => String(n).padStart(2, '0');
      const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      a.download = `共享数据_${ts}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      message.success('导出成功');
    } catch {
      message.error('导出失败');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    setLoading(true);
    try {
      const { data } = await api.post('/slow-inventory/import', fd);
      message.success(data.message || '导入完成');
      setImportOpen(false);
      fetchData(activeTab, filterModel);
    } catch (e) {
      message.error(e.response?.data?.detail || '导入失败');
    } finally {
      setLoading(false);
    }
    return false;
  };

  const tabItems = [
    {
      key: 'my',
      label: '我的数据',
      children: (
        <Table
          rowKey="id" size="middle" bordered loading={loading}
          dataSource={myData} columns={columns(myData, true)}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 20, showSizeChanger: false, showTotal: (t) => `共 ${t} 条` }}
        />
      ),
    },
    {
      key: 'shared',
      label: '共享数据',
      children: (
        <Table
          rowKey="id" size="middle" bordered loading={loading}
          dataSource={sharedData} columns={columns(sharedData, false)}
          scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 20, showSizeChanger: false, showTotal: (t) => `共 ${t} 条` }}
        />
      ),
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16 }}>滞销库存</Title>

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap size="middle">
          <span style={{ color: '#666' }}>型号：</span>
          <Input
            value={filterModel}
            onChange={(e) => setFilterModel(e.target.value)}
            onPressEnter={handleSearch}
            placeholder="请输入型号"
            allowClear
            style={{ width: 220 }}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
          {activeTab === 'my' && (
            <>
              {/* 同步按钮临时下线 */}
              <Button type="primary" ghost icon={<PlusOutlined />} onClick={openAdd}>新增</Button>
              <Button icon={<UploadOutlined />} onClick={() => setImportOpen(true)}>批量导入</Button>
            </>
          )}
          {activeTab === 'shared' && (
            <Button icon={<DownloadOutlined />} loading={exporting} onClick={handleExportShared}>数据导出</Button>
          )}
        </Space>
      </Card>

      <Card size="small">
        <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} />
      </Card>

      {/* 新增/编辑弹窗 */}
      <Modal
        title={isEdit ? '编辑' : '新增'}
        open={dialogOpen}
        onOk={handleSubmit}
        onCancel={() => setDialogOpen(false)}
        confirmLoading={saving}
        destroyOnClose
        okText={isEdit ? '保存' : '新增'}
        cancelText="取消"
        width={520}
      >
        <Form form={form} labelCol={{ span: 5 }} wrapperCol={{ span: 18 }} layout="horizontal">
          <Form.Item name="__id" hidden><Input /></Form.Item>
          <Form.Item label="型号" name="model" rules={[{ required: true, message: '请输入型号' }]}>
            <Input placeholder="请输入型号" maxLength={100} />
          </Form.Item>
          <Form.Item label="数量" name="number" rules={[{ required: true, message: '请输入数量' }]}>
            <InputNumber min={0} max={99999999} step={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label="效期"
            name="valid"
            rules={[
              { required: true, message: '请输入效期' },
              {
                validator: (_, v) => {
                  const s = (v || '').trim();
                  if (!s) return Promise.resolve();
                  const m = s.match(/^(\d{4})[-./ ](\d{1,2})[-./ ](\d{1,2})/) || s.match(/^(\d{4})(\d{2})(\d{2})\b/);
                  if (!m) return Promise.reject(new Error('效期格式不合法，如 2026-11-21 / 20261121'));
                  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
                  if (d.getFullYear() !== Number(m[1]) || d.getMonth() !== Number(m[2]) - 1 || d.getDate() !== Number(m[3])) {
                    return Promise.reject(new Error('效期不是有效日期'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input placeholder="如 2026-11-21 或 20261121" maxLength={100} />
          </Form.Item>
          <Form.Item label="备注" name="remark">
            <Input.TextArea rows={3} placeholder="请输入备注" maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>

      {/* 批量导入弹窗 */}
      <Modal
        title="批量导入"
        open={importOpen}
        onCancel={() => setImportOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setImportOpen(false)}>取消</Button>,
          <Button key="ok" type="primary" onClick={() => setImportOpen(false)}>确定</Button>,
        ]}
        width={520}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Text>导入模板：</Text>
            <Button size="small" icon={<DownloadOutlined />} onClick={handleDownload}>点击下载</Button>
          </div>
          <div>
            <Text type="danger">
              导入说明：1、请下载【滞销库存导入模板】并批量导入；2、不要为数据设置任何格式！3、型号、数量、效期为必填项！
            </Text>
          </div>
          <Upload.Dragger accept=".xls,.xlsx" showUploadList={false} beforeUpload={handleImport}>
            <p className="ant-upload-drag-icon"><UploadOutlined /></p>
            <p className="ant-upload-text">将文件拖到此处，或点击上传</p>
            <p className="ant-upload-hint">请上传 .xls / .xlsx 标准格式文件</p>
          </Upload.Dragger>
        </Space>
      </Modal>
    </div>
  );
}
